import { ref } from 'vue';

export type DragPositionType = 'top' | 'bottom' | 'center' | null;

export type DragAndDropOptionsType<T> = {
    treeContainerClass: string;
    itemSelector: string;
    nameSelector: string;
    getItemData: (element: HTMLElement) => T | null;
    canDrag: (item: T) => boolean;
    canDrop: (draggedItem: T, targetItem: T | null, position: DragPositionType) => boolean;
    onDrop: (draggedItem: T, targetItem: T | null, position: DragPositionType) => Promise<boolean>;
    onAutoExpand?: (item: T) => void;
    onError?: (error: Error) => void;
};

export const useDragAndDrop = <T extends { id: number | string; name: string }>(options: DragAndDropOptionsType<T>) => {
    const draggedItem = ref<T | null>(null);
    const dragOverItem = ref<T | null>(null);
    const dragPosition = ref<DragPositionType>(null);
    const isDragging = ref(false);

    let autoExpandTimeout: NodeJS.Timeout | null = null;
    let lastHoveredItem: T | null = null;

    const handleDragStart = (event: DragEvent, item: T) => {
        if (!options.canDrag(item)) {
            event.preventDefault();

            return;
        }

        draggedItem.value = item;
        isDragging.value = true;

        if (event.dataTransfer) {
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData('text/plain', String(item.id));
        }

        const element = event.currentTarget as HTMLElement;
        element.classList.add('dragging');
    };

    const calculateDropPosition = (event: DragEvent): DragPositionType => {
        const target = (event.target as HTMLElement).closest<HTMLElement>('.v-list-item');
        if (!target) {
            return 'center';
        }

        const rect = target.getBoundingClientRect();
        const y = event.clientY - rect.top;
        const height = rect.height;
        const threshold = height / 3;

        if (y < threshold) {
            return 'top';
        }
        if (y > height - threshold) {
            return 'bottom';
        }

        return 'center';
    };

    const handleDragOver = (event: DragEvent, item: T | null) => {
        event.preventDefault();
        event.stopPropagation();

        if (!draggedItem.value || (item && draggedItem.value.id === item.id)) {
            return;
        }

        const position = item ? calculateDropPosition(event) : 'center';

        if (!options.canDrop(draggedItem.value, item, position)) {
            return;
        }

        dragOverItem.value = item;
        dragPosition.value = position;

        if (event.dataTransfer) {
            event.dataTransfer.dropEffect = 'move';
        }
    };

    const clearAutoExpandTimeout = () => {
        if (autoExpandTimeout) {
            clearTimeout(autoExpandTimeout);
            autoExpandTimeout = null;
        }
    };

    const handleAutoExpand = (item: T, position: DragPositionType) => {
        if (position === 'center' && options.onAutoExpand && item.id !== lastHoveredItem?.id) {
            clearAutoExpandTimeout();
            lastHoveredItem = item;

            autoExpandTimeout = setTimeout(() => {
                if (dragOverItem.value?.id === item.id && dragPosition.value === 'center') {
                    options.onAutoExpand!(item);
                }
            }, 300);
        } else if (position !== 'center' || item.id !== lastHoveredItem?.id) {
            clearAutoExpandTimeout();
            lastHoveredItem = null;
        }
    };

    const handleDragLeave = () => {
        clearAutoExpandTimeout();
        lastHoveredItem = null;
        dragOverItem.value = null;
        dragPosition.value = null;
    };

    const handleDrop = async (event: DragEvent, item: T | null) => {
        event.preventDefault();
        event.stopPropagation();

        if (!draggedItem.value || !dragPosition.value) {
            return;
        }

        if (item && draggedItem.value.id === item.id) {
            return;
        }

        const draggedNode = draggedItem.value;
        const targetNode = item;
        const position = dragPosition.value;

        if (!options.canDrop(draggedNode, targetNode, position)) {
            handleDragEnd();

            return;
        }
        try {
            const success = await options.onDrop(draggedNode, targetNode, position);
            if (!success && options.onError) {
                options.onError(new Error('Drop operation failed'));
            }
        } catch (error) {
            if (options.onError) {
                options.onError(error as Error);
            }
        }

        handleDragEnd();
    };

    const handleDragEnd = () => {
        clearAutoExpandTimeout();
        lastHoveredItem = null;
        draggedItem.value = null;
        dragOverItem.value = null;
        dragPosition.value = null;
        isDragging.value = false;

        cleanupDragClasses();
    };

    const cleanupDragClasses = () => {
        const treeElement = document.querySelector(`.${options.treeContainerClass}`);
        if (treeElement) {
            const allItems = Array.from(treeElement.querySelectorAll(options.itemSelector));
            for (const el of allItems) {
                el.classList.remove('dragging', 'drag-over-top', 'drag-over-bottom', 'drag-over-center');
            }
        }
    };

    const handleGlobalDragStart = (event: DragEvent) => {
        const target = event.target as HTMLElement;
        const listItem = target.closest<HTMLElement>(options.itemSelector);
        if (!listItem) {
            return;
        }

        const itemData = options.getItemData(listItem);
        if (itemData && options.canDrag(itemData)) {
            handleDragStart(event, itemData);
        }
    };

    const handleGlobalDragEnd = () => {
        handleDragEnd();
    };

    const handleGlobalDragOver = (event: DragEvent) => {
        const target = event.target as HTMLElement;
        const treeContainer = target.closest<HTMLElement>(`.${options.treeContainerClass}`);
        const listItem = target.closest<HTMLElement>(options.itemSelector);

        if (listItem) {
            const itemData = options.getItemData(listItem);
            if (itemData) {
                handleDragOver(event, itemData);

                handleAutoExpand(itemData, dragPosition.value);

                cleanupDragClasses();

                if (dragPosition.value === 'top') {
                    listItem.classList.add('drag-over-top');
                } else if (dragPosition.value === 'bottom') {
                    listItem.classList.add('drag-over-bottom');
                } else if (dragPosition.value === 'center') {
                    listItem.classList.add('drag-over-center');
                }
            }

            return;
        }

        if (treeContainer && draggedItem.value) {
            handleDragOver(event, null);
        } else {
            clearAutoExpandTimeout();
            lastHoveredItem = null;
        }
    };

    const handleGlobalDragLeave = (event: DragEvent) => {
        const target = event.target as HTMLElement;
        const listItem = target.closest<HTMLElement>(options.itemSelector);
        if (!listItem) {
            return;
        }

        const relatedTarget = event.relatedTarget as HTMLElement;
        if (relatedTarget && listItem.contains(relatedTarget)) {
            return;
        }

        handleDragLeave();
        listItem.classList.remove('drag-over-top', 'drag-over-bottom', 'drag-over-center');
    };

    const handleGlobalDrop = async (event: DragEvent) => {
        const target = event.target as HTMLElement;
        const treeContainer = target.closest<HTMLElement>(`.${options.treeContainerClass}`);
        const listItem = target.closest<HTMLElement>(options.itemSelector);

        if (listItem) {
            const itemData = options.getItemData(listItem);
            if (itemData) {
                await handleDrop(event, itemData);
            }

            return;
        }

        if (treeContainer && draggedItem.value) {
            await handleDrop(event, null);
        }
    };

    const initializeDragAndDrop = () => {
        const treeElement = document.querySelector<HTMLElement>(`.${options.treeContainerClass}`);
        if (!treeElement) {
            return;
        }

        destroyDragAndDrop();

        treeElement.addEventListener('dragstart', handleGlobalDragStart);
        treeElement.addEventListener('dragend', handleGlobalDragEnd);
        treeElement.addEventListener('dragover', handleGlobalDragOver);
        treeElement.addEventListener('dragleave', handleGlobalDragLeave);
        treeElement.addEventListener('drop', handleGlobalDrop);

        const treeItems = Array.from(treeElement.querySelectorAll<HTMLElement>(options.itemSelector));

        for (const itemElement of treeItems) {
            const nodeNameElement = itemElement.querySelector(options.nameSelector);
            if (!nodeNameElement) {
                continue;
            }

            const itemData = options.getItemData(itemElement);

            if (itemData && options.canDrag(itemData)) {
                itemElement.draggable = true;
                itemElement.style.cursor = 'move';
            }
        }
    };

    const destroyDragAndDrop = () => {
        const treeElement = document.querySelector<HTMLElement>(`.${options.treeContainerClass}`);
        if (treeElement) {
            treeElement.removeEventListener('dragstart', handleGlobalDragStart);
            treeElement.removeEventListener('dragend', handleGlobalDragEnd);
            treeElement.removeEventListener('dragover', handleGlobalDragOver);
            treeElement.removeEventListener('dragleave', handleGlobalDragLeave);
            treeElement.removeEventListener('drop', handleGlobalDrop);
        }
    };

    return {
        draggedItem,
        dragOverItem,
        dragPosition,
        isDragging,
        initializeDragAndDrop,
        destroyDragAndDrop,
    };
};
